import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon, FileText as FileTextIcon, History as HistoryIcon, RotateCw as RotateCwIcon, Trash2 as Trash2Icon, Pencil as PencilIcon, Save as SaveIcon, X as XIcon } from 'lucide-react';
import medicoVfpService from '../../services/medicoVfpService';
import { recetasService, Receta } from '../../services/recetasService';

function formatMexicoCityDate(now: Date) {
  const tz = 'America/Mexico_City';
  const parts = new Intl.DateTimeFormat('es-MX', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).formatToParts(now);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  const month = get('month').toUpperCase();
  return `${get('day')} ${month} ${get('year')}`;
}

function calcularEdad(nacimiento: string) {
  if (!nacimiento) return { anios: 0, meses: 0, dias: 0 };
  const birth = new Date(nacimiento);
  if (Number.isNaN(birth.getTime())) return { anios: 0, meses: 0, dias: 0 };
  const today = new Date();
  let y = today.getFullYear() - birth.getFullYear();
  let m = today.getMonth() - birth.getMonth();
  let d = today.getDate() - birth.getDate();
  if (d < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    d += prevMonth.getDate();
    m -= 1;
  }
  if (m < 0) {
    m += 12;
    y -= 1;
  }
  return { anios: Math.max(0, y), meses: Math.max(0, m), dias: Math.max(0, d) };
}

const PERCENTIL_LABELS: Record<string, string> = {
  muy_bajo: 'Muy bajo (< P3)',
  bajo: 'Bajo (P3-P15)',
  normal_bajo: 'Normal bajo (P15-P50)',
  normal: 'Normal (P50-P85)',
  alto: 'Alto (P85-P97)',
  muy_alto: 'Muy alto (> P97)',
};

const MAX_RECETA_LINES_BENEFICENCIA = 21;
const MAX_RECETA_LINES_ANGELES = 21;
const WARNING_RECETA_LINES_BENEFICENCIA = 19;
const WARNING_RECETA_LINES_ANGELES = 19;

export default function RecetaRapidaPage() {
  const navigate = useNavigate();

  const [apellido1, setApellido1] = useState('');
  const [apellido2, setApellido2] = useState('');
  const [nombre, setNombre] = useState('');
  const [sexo, setSexo] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [peso, setPeso] = useState('');
  const [talla, setTalla] = useState('');
  const [recetaTexto, setRecetaTexto] = useState('');
  const [recipeFormat, setRecipeFormat] = useState<'beneficencia' | 'angeles'>(() => {
    const saved = localStorage.getItem('recipeFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });

  const [percentilPeso, setPercentilPeso] = useState<string>('');
  const [percentilTalla, setPercentilTalla] = useState<string>('');
  const [guardarPaciente, setGuardarPaciente] = useState(false);
  const [calculandoPercentiles, setCalculandoPercentiles] = useState(false);
  const [activeTab, setActiveTab] = useState<'nueva' | 'previas'>('nueva');
  const [recetasPrevias, setRecetasPrevias] = useState<Receta[]>([]);
  const [editingReceta, setEditingReceta] = useState<Receta | null>(null);

  const loadRecetasPrevias = useCallback(async () => {
    try {
      const result = await recetasService.findAll({ take: 20 });
      setRecetasPrevias(result.data);
    } catch (e) {
      console.error('Error al cargar recetas previas:', e);
    }
  }, []);

  useEffect(() => {
    loadRecetasPrevias();
  }, [loadRecetasPrevias]);

  const edad = useMemo(() => calcularEdad(fechaNacimiento), [fechaNacimiento]);

  const edadTexto = useMemo(() => {
    if (!fechaNacimiento) return '';
    return `${edad.anios} años ${edad.meses} meses ${edad.dias} días`;
  }, [edad, fechaNacimiento]);

  const nombreCompleto = useMemo(() => {
    return [nombre, apellido1].filter(Boolean).join(' ').trim();
  }, [nombre, apellido1]);

  // Calcular percentiles automáticamente
  useEffect(() => {
    if (!sexo || !fechaNacimiento || (!peso && !talla)) {
      // En modo edición conservar los percentiles guardados en la receta
      if (!editingReceta) {
        setPercentilPeso('');
        setPercentilTalla('');
      }
      return;
    }
    const pesoNum = peso ? parseFloat(peso) : undefined;
    const tallaNum = talla ? parseFloat(talla) : undefined;
    if ((pesoNum === undefined || isNaN(pesoNum)) && (tallaNum === undefined || isNaN(tallaNum))) {
      if (!editingReceta) {
        setPercentilPeso('');
        setPercentilTalla('');
      }
      return;
    }

    setCalculandoPercentiles(true);
    const timer = setTimeout(async () => {
      try {
        const result = await medicoVfpService.calcularPercentiles({
          sexo,
          anos: edad.anios,
          meses: edad.meses,
          peso: pesoNum,
          talla: tallaNum,
        });
        setPercentilPeso(result.peso ? (PERCENTIL_LABELS[result.peso] || '') : '');
        setPercentilTalla(result.talla ? (PERCENTIL_LABELS[result.talla] || '') : '');
      } catch (e) {
        console.error('Error al calcular percentiles:', e);
        setPercentilPeso('');
        setPercentilTalla('');
      } finally {
        setCalculandoPercentiles(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [sexo, fechaNacimiento, peso, talla, edad.anios, edad.meses, editingReceta]);

  const recetaLines = useMemo(() => {
    if (!recetaTexto) return 0;
    return recetaTexto.split('\n').length;
  }, [recetaTexto]);

  const maxRecetaLines = recipeFormat === 'angeles' ? MAX_RECETA_LINES_ANGELES : MAX_RECETA_LINES_BENEFICENCIA;
  const warningRecetaLines = recipeFormat === 'angeles' ? WARNING_RECETA_LINES_ANGELES : WARNING_RECETA_LINES_BENEFICENCIA;

  const recetaStatus = useMemo(() => {
    if (recetaLines > maxRecetaLines) {
      return { type: 'error', message: `⚠️ Excede el espacio (${recetaLines}/${maxRecetaLines} líneas)` };
    }
    if (recetaLines >= warningRecetaLines) {
      return { type: 'warning', message: `⚡ Cerca del límite (${recetaLines}/${maxRecetaLines} líneas)` };
    }
    return { type: 'ok', message: `✓ ${recetaLines}/${maxRecetaLines} líneas` };
  }, [recetaLines, maxRecetaLines, warningRecetaLines]);

  const handleEditarReceta = (receta: Receta) => {
    if (recetaTexto.trim() && !editingReceta) {
      const continuar = window.confirm('El texto actual de la receta se descartará. ¿Desea continuar?');
      if (!continuar) return;
    }
    setEditingReceta(receta);
    // nombrePaciente es un solo texto: se carga completo en el campo Nombre(s)
    setNombre(receta.nombrePaciente || '');
    setApellido1('');
    setApellido2('');
    setSexo('');
    setFechaNacimiento(receta.fechaNacimiento ? receta.fechaNacimiento.slice(0, 10) : '');
    setPeso(receta.peso != null ? String(receta.peso) : '');
    setTalla(receta.talla != null ? String(receta.talla) : '');
    setPercentilPeso(receta.percentilePeso || '');
    setPercentilTalla(receta.percentileTalla || '');
    setRecetaTexto(receta.medicamentos || '');
    if (receta.formato === 'beneficencia' || receta.formato === 'angeles') {
      setRecipeFormat(receta.formato);
    }
    setActiveTab('nueva');
  };

  const handleCancelarEdicion = () => {
    setEditingReceta(null);
    setRecetaTexto('');
  };

  const handleGuardarEdicion = async (imprimir: boolean) => {
    if (!editingReceta) return;
    if (!nombreCompleto) {
      toast.error('Capture al menos el nombre del paciente');
      return;
    }
    if (imprimir && recetaLines > maxRecetaLines) {
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: La receta tiene ${recetaLines} líneas, pero solo caben ${maxRecetaLines}.\n\n¿Desea continuar?`
      );
      if (!confirmar) return;
    }

    try {
      const recetaActualizada = await recetasService.update(editingReceta.id, {
        nombrePaciente: nombreCompleto,
        fechaNacimiento: fechaNacimiento || undefined,
        peso: peso ? parseFloat(peso) : undefined,
        talla: talla ? parseFloat(talla) : undefined,
        percentilePeso: percentilPeso || undefined,
        percentileTalla: percentilTalla || undefined,
        edadTexto: edadTexto || undefined,
        medicamentos: recetaTexto || undefined,
        formato: recipeFormat,
      });
      toast.success('Receta actualizada');
      loadRecetasPrevias();

      if (imprimir) {
        navigate('/receta-rapida/print', {
          state: {
            nombrePaciente: recetaActualizada.nombrePaciente || '',
            edadTexto: recetaActualizada.edadTexto || '',
            peso: recetaActualizada.peso != null ? String(recetaActualizada.peso) : '',
            talla: recetaActualizada.talla != null ? String(recetaActualizada.talla) : '',
            percentilPeso: recetaActualizada.percentilePeso || '',
            percentilTalla: recetaActualizada.percentileTalla || '',
            prescription: recetaActualizada.medicamentos || '',
            fecha: formatMexicoCityDate(new Date(recetaActualizada.fecha)),
            formato: recetaActualizada.formato || 'beneficencia',
            recetaToken: recetaActualizada.token,
          },
        });
      }
    } catch (e) {
      console.error('Error al actualizar receta:', e);
      toast.error('No se pudo actualizar la receta');
    }
  };

  const handleBorrarReceta = async (id: number) => {
    if (!window.confirm('¿Está seguro de borrar esta receta? Esta acción no se puede deshacer.')) return;
    try {
      await recetasService.remove(id);
      toast.success('Receta borrada');
      loadRecetasPrevias();
    } catch (e) {
      console.error('Error al borrar receta:', e);
      toast.error('No se pudo borrar la receta');
    }
  };

  const handleTabKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 1;
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
    }
  };

  const handleImprimir = async () => {
    if (!nombreCompleto) {
      toast.error('Capture al menos el nombre del paciente');
      return;
    }
    if (recetaLines > maxRecetaLines) {
      const confirmar = window.confirm(
        `⚠️ ADVERTENCIA: La receta tiene ${recetaLines} líneas, pero solo caben ${maxRecetaLines}.\n\n¿Desea continuar?`
      );
      if (!confirmar) return;
    }

    try {
      const fechaMx = formatMexicoCityDate(new Date());
      let savedRecetaToken: string | null = null;

      // Solo guardar en BD si se activó "Guardar paciente"
      if (guardarPaciente) {
        // Buscar si el paciente ya existe por nombre
        let pacienteExistenteId: number | null = null;
        try {
          // Buscar por el primer campo no vacío como apellido1
          // (el paciente pudo haberse registrado con los campos en orden distinto)
          const primerCampo = [apellido1, apellido2, nombre].find(v => v && v.trim()) || '';
          const result = await medicoVfpService.getPacientesVfp({
            searchApellido1: primerCampo,
            take: 10,
          });
          if (result.data && result.data.length > 0) {
            const buscar = (s: string) => (s || '').trim().toUpperCase();
            const camposBuscados = [buscar(apellido1), buscar(apellido2), buscar(nombre)].filter(Boolean);
            const match = result.data.find(p => {
              const camposPaciente = [buscar(p.apellido1 || ''), buscar(p.apellido2 || ''), buscar(p.nombre || '')].filter(Boolean);
              return camposBuscados.every(c => camposPaciente.includes(c));
            });
            if (match) pacienteExistenteId = match.clave;
          }
        } catch (e) {
          console.error('Error al buscar paciente existente:', e);
        }

        if (pacienteExistenteId) {
          const confirmar = window.confirm(
            `El paciente "${nombreCompleto}" ya existe en el sistema.\n\n` +
            `Se guardará la receta en el historial del paciente existente y se procederá a imprimir.\n\n` +
            `¿Desea continuar?`
          );
          if (!confirmar) return;

          // Guardar receta vinculada al paciente existente (no crear nuevo)
          const recetaCreada = await recetasService.create({
            pacienteId: pacienteExistenteId,
            nombrePaciente: nombreCompleto,
            fechaNacimiento: fechaNacimiento || undefined,
            peso: peso ? parseFloat(peso) : undefined,
            talla: talla ? parseFloat(talla) : undefined,
            percentilePeso: percentilPeso || undefined,
            percentileTalla: percentilTalla || undefined,
            edadTexto: edadTexto || undefined,
            medicamentos: recetaTexto || undefined,
            formato: recipeFormat,
          });
          savedRecetaToken = recetaCreada.token;
          toast.success('Receta guardada en el historial del paciente existente');
        } else {
          // No existe, crear paciente nuevo
          const recetaCreada2 = await recetasService.create({
            nombrePaciente: nombreCompleto,
            fechaNacimiento: fechaNacimiento || undefined,
            peso: peso ? parseFloat(peso) : undefined,
            talla: talla ? parseFloat(talla) : undefined,
            percentilePeso: percentilPeso || undefined,
            percentileTalla: percentilTalla || undefined,
            edadTexto: edadTexto || undefined,
            medicamentos: recetaTexto || undefined,
            formato: recipeFormat,
            guardarPaciente,
            apellido1: apellido1 || undefined,
            apellido2: apellido2 || undefined,
            nombre: nombre || undefined,
            sexo: sexo || undefined,
            procedencia: recipeFormat || undefined,
          });
          savedRecetaToken = recetaCreada2.token;
          toast.success('Receta guardada y paciente registrado');
        }
        loadRecetasPrevias();
      } else {
        // Guardar receta sin vincular paciente, pero sí generar token para QR
        const recetaCreada = await recetasService.create({
          nombrePaciente: nombreCompleto,
          fechaNacimiento: fechaNacimiento || undefined,
          peso: peso ? parseFloat(peso) : undefined,
          talla: talla ? parseFloat(talla) : undefined,
          percentilePeso: percentilPeso || undefined,
          percentileTalla: percentilTalla || undefined,
          edadTexto: edadTexto || undefined,
          medicamentos: recetaTexto || undefined,
          formato: recipeFormat,
        });
        savedRecetaToken = recetaCreada.token;
      }

      navigate('/receta-rapida/print', {
        state: {
          nombrePaciente: nombreCompleto,
          nombre,
          apellido1,
          edadTexto,
          peso,
          talla,
          percentilPeso,
          percentilTalla,
          prescription: recetaTexto,
          fecha: fechaMx,
          formato: recipeFormat,
          recetaToken: savedRecetaToken,
        },
      });
    } catch (error) {
      console.error('Error al guardar receta:', error);
      toast.error('No se pudo guardar la receta');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow sticky top-0 z-20">
        <div className="max-w-full mx-auto px-2 sm:px-3 lg:px-4 py-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Volver
            </button>
            <div className="h-5 w-px bg-gray-300"></div>
            <h1 className="text-sm font-semibold text-gray-900">Receta Rápida</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-2 sm:px-3 lg:px-4 py-4">
        {/* Datos del paciente */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="text-sm font-semibold text-gray-900 mb-3">Datos del paciente</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Apellido paterno</label>
              <input
                type="text"
                value={apellido1}
                onChange={e => setApellido1(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="Apellido paterno"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Apellido materno</label>
              <input
                type="text"
                value={apellido2}
                onChange={e => setApellido2(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="Apellido materno"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre(s)</label>
              <input
                type="text"
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="Nombre(s)"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sexo</label>
              <select
                value={sexo}
                onChange={e => setSexo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              >
                <option value="">Seleccionar...</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de nacimiento</label>
              <input
                type="date"
                value={fechaNacimiento}
                onChange={e => setFechaNacimiento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Peso (kg)</label>
              <input
                type="number"
                step="0.001"
                value={peso}
                onChange={e => setPeso(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="0.0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Talla (cm)</label>
              <input
                type="number"
                step="0.1"
                value={talla}
                onChange={e => setTalla(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder="0.0"
              />
            </div>
          </div>

          {/* Info calculada */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-md px-3 py-2">
              <span className="text-xs text-gray-500">Edad: </span>
              <span className="text-sm font-semibold text-gray-900">{edadTexto || '—'}</span>
            </div>
            <div className="bg-gray-50 rounded-md px-3 py-2">
              <span className="text-xs text-gray-500">Percentil peso: </span>
              <span className="text-sm font-semibold text-gray-900">
                {calculandoPercentiles ? 'calculando...' : (percentilPeso || '—')}
              </span>
            </div>
            <div className="bg-gray-50 rounded-md px-3 py-2">
              <span className="text-xs text-gray-500">Percentil talla: </span>
              <span className="text-sm font-semibold text-gray-900">
                {calculandoPercentiles ? 'calculando...' : (percentilTalla || '—')}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex gap-2 border-b border-gray-200 mb-4">
            <button
              onClick={() => setActiveTab('nueva')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'nueva'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileTextIcon className="w-4 h-4" />
              Receta Nueva
            </button>
            <button
              onClick={() => setActiveTab('previas')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'previas'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <HistoryIcon className="w-4 h-4" />
              Recetas Previas ({recetasPrevias.length})
            </button>
          </div>

          {activeTab === 'nueva' ? (
            <>
        {/* Formato y receta */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Receta
              {editingReceta && (
                <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                  Editando receta del {formatMexicoCityDate(new Date(editingReceta.fecha))}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <select
                value={recipeFormat}
                onChange={e => {
                  const val = e.target.value as 'beneficencia' | 'angeles';
                  setRecipeFormat(val);
                  localStorage.setItem('recipeFormat', val);
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm"
              >
                <option value="beneficencia">Beneficencia Española</option>
                <option value="angeles">Hospital Ángeles</option>
              </select>
              <div
                className={`text-xs font-semibold px-2 py-1 rounded ${
                  recetaStatus.type === 'error'
                    ? 'bg-red-100 text-red-700'
                    : recetaStatus.type === 'warning'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-green-100 text-green-700'
                }`}
              >
                {recetaStatus.message}
              </div>
            </div>
          </div>
          <textarea
            rows={10}
            value={recetaTexto}
            onChange={e => setRecetaTexto(e.target.value)}
            onKeyDown={handleTabKey}
            style={{ tabSize: 38 }}
            className={`w-full p-3 border rounded-md focus:ring-2 font-mono font-bold text-base leading-snug resize-none ${
              recetaStatus.type === 'error'
                ? 'border-red-400 bg-red-50 focus:ring-red-500 focus:border-red-500'
                : recetaStatus.type === 'warning'
                ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500'
                : 'border-gray-300 focus:ring-purple-500 focus:border-transparent'
            }`}
            placeholder="Escriba los medicamentos..."
          />
        </div>

        {/* Opción guardar paciente (no aplica al editar una receta existente) */}
        {!editingReceta && (
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={guardarPaciente}
              onChange={e => setGuardarPaciente(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Guardar paciente en el sistema al imprimir</span>
          </label>
        </div>
        )}

        {/* Botones */}
        <div className="flex items-center justify-end gap-2 py-2">
          {editingReceta ? (
            <>
              <button
                onClick={handleCancelarEdicion}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
              >
                <XIcon className="w-4 h-4" />
                Cancelar
              </button>
              <button
                onClick={() => handleGuardarEdicion(false)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                <SaveIcon className="w-4 h-4" />
                Guardar cambios
              </button>
              <button
                onClick={() => handleGuardarEdicion(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
              >
                <PrinterIcon className="w-4 h-4" />
                Guardar e imprimir
              </button>
            </>
          ) : (
          <button
            onClick={handleImprimir}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
          >
            <PrinterIcon className="w-4 h-4" />
            Imprimir
          </button>
          )}
        </div>
            </>
          ) : (
            <div className="space-y-2">
              {recetasPrevias.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No hay recetas previas
                </div>
              ) : (
                recetasPrevias.map((receta) => {
                  const fecha = new Date(receta.fecha);
                  const fechaStr = fecha.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
                  const horaStr = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                  const preview = receta.medicamentos?.substring(0, 80) || '(sin medicamentos)';
                  return (
                    <div
                      key={receta.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900">
                          {fechaStr} - {horaStr}
                          {receta.nombrePaciente && <span className="text-gray-500 font-normal ml-2">| {receta.nombrePaciente}</span>}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          {preview}{receta.medicamentos && receta.medicamentos.length > 80 ? '...' : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/receta-rapida/print', {
                            state: {
                              nombrePaciente: receta.nombrePaciente || '',
                              edadTexto: receta.edadTexto || '',
                              peso: receta.peso != null ? String(receta.peso) : '',
                              talla: receta.talla != null ? String(receta.talla) : '',
                              percentilPeso: receta.percentilePeso || '',
                              percentilTalla: receta.percentileTalla || '',
                              prescription: receta.medicamentos || '',
                              fecha: formatMexicoCityDate(fecha),
                              formato: receta.formato || 'beneficencia',
                              recetaToken: receta.token,
                            },
                          });
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors ml-2"
                      >
                        <RotateCwIcon className="w-3.5 h-3.5" />
                        Reimprimir
                      </button>
                      <button
                        onClick={() => handleEditarReceta(receta)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors ml-1"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleBorrarReceta(receta.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors ml-1"
                      >
                        <Trash2Icon className="w-3.5 h-3.5" />
                        Borrar
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
